<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-emerald-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-emerald-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Herramientas del Taller
          <span class="px-2 sm:px-3 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 text-xs sm:text-sm font-black rounded-lg border border-emerald-200 dark:border-emerald-800">
            {{ herramientasTaller.length }}
          </span>
        </h1>
      </div>
      <!-- Botón Nueva Herramienta -->
      <button 
        @click="openCreateModal"
        class="w-full sm:w-auto px-3 sm:px-5 py-2 sm:py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center justify-center gap-2"
      >
        <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
        <span class="hidden sm:inline">Nueva Herramienta</span>
        <span class="sm:hidden">Nuevo</span>
      </button>
    </div>

    <!-- Filters & Search -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-7 lg:col-span-8">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Búsqueda Inteligente</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-emerald-500 transition-colors" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar por código, nombre o sede..."
              class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base"
            />
          </div>
        </div>
        
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filtroEstado"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base"
          >
            <option value="">TODOS</option>
            <option value="Activo">ACTIVO</option>
            <option value="Mantenimiento">MANTENIMIENTO</option>
            <option value="Inactivo">INACTIVO</option>
          </select>
        </div>

        <div class="sm:col-span-2 lg:col-span-2">
          <button 
            @click="fetchHerramientasTaller"
            class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm"
          >
            <Icon icon="mdi:refresh" :class="{'animate-spin': cargando}" class="text-lg sm:text-xl" />
            <span class="hidden sm:inline">Refrescar</span>
            <span class="sm:hidden">Refrescar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-5 animate-fadeInUp delay-100">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Total</p>
        <p class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">{{ herramientasTaller.length }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Activas</p>
        <p class="text-xl sm:text-2xl font-black text-emerald-600">{{ herramientasActivas }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4 text-center">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Mantenimiento</p>
        <p class="text-xl sm:text-2xl font-black text-amber-600">{{ herramientasMantenimiento }}</p>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-200">
      <!-- Versión Desktop: Tabla completa -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Código</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Herramienta</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Sede</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Stock</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="cargando" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="6" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!cargando && filteredHerramientas.length === 0">
              <td colspan="6" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:tools-off" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay herramientas</h4>
                  <p class="text-sm text-gray-300 mt-1">Registra tu primera herramienta</p>
                </div>
              </td>
            </tr>
            <tr v-for="h in paginatedHerramientas" :key="h.idHerramienta" class="hover:bg-emerald-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg font-black text-xs border border-emerald-100 dark:border-emerald-800 transition-transform inline-block">
                  {{ h.codigo }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="text-left">
                  <p class="font-black text-gray-900 dark:text-white text-xs mb-1">{{ h.nombre }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]" :title="h.descripcion">
                    {{ h.descripcion || 'Sin descripción' }}
                  </p>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-2">
                  <Icon icon="mdi:store-marker" class="text-amber-500 text-base" />
                  <span class="font-black text-gray-700 dark:text-gray-300 text-xs">{{ h.marca || '-' }}</span>
                </div>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="['text-sm font-black px-3 py-1 rounded-lg', h.stock > 0 ? 'text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700' : 'text-rose-500 bg-rose-50 dark:bg-rose-900/30']">
                  {{ h.stock }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="[
                  'px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border shadow-sm inline-block',
                  h.estado === 'Activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' :
                  h.estado === 'Mantenimiento' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' :
                  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
                ]">
                  {{ h.estado }}
                </span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1 flex-nowrap">
                  <button @click="openEditModal(h)" title="Editar" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button @click="openInfoModal(h)" title="Ver información" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm">
                    <Icon icon="mdi:information-outline" class="text-base" />
                  </button>
                  <button @click="confirmDeleteHerramienta(h)" title="Eliminar" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm">
                    <Icon icon="mdi:delete" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-if="cargando" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!cargando && filteredHerramientas.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:tools-off" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay herramientas</h4>
          </div>
        </div>
        
        <div v-for="h in paginatedHerramientas" :key="h.idHerramienta" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg font-black text-xs border border-emerald-100 dark:border-emerald-800">
                {{ h.codigo }}
              </span>
              <span :class="[
                'px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border shadow-sm',
                h.estado === 'Activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' :
                h.estado === 'Mantenimiento' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' :
                'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
              ]">
                {{ h.estado }}
              </span>
            </div>
            <div class="text-xs font-black text-gray-700 dark:text-gray-300">
              <span class="text-gray-400 font-normal">Stock:</span> {{ h.stock }}
            </div>
          </div>

          <div class="mb-2">
            <p class="font-black text-gray-900 dark:text-white text-sm">{{ h.nombre }}</p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 italic truncate">{{ h.descripcion || 'Sin descripción' }}</p>
          </div>

          <div class="flex items-center gap-2 mb-3 bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <Icon icon="mdi:store-marker" class="text-amber-500 text-base" />
            <span class="font-black text-gray-700 dark:text-gray-300 text-xs">{{ h.marca || 'Sin sede' }}</span>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="openEditModal(h)" title="Editar" class="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
              <Icon icon="mdi:pencil" class="text-sm" />
              <span class="text-[8px] font-black sm:hidden">Editar</span>
            </button>
            <button @click="openInfoModal(h)" title="Ver información" class="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
              <Icon icon="mdi:information-outline" class="text-sm" />
              <span class="text-[8px] font-black sm:hidden">Info</span>
            </button>
            <button @click="confirmDeleteHerramienta(h)" title="Eliminar" class="p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-1 sm:flex-none flex items-center justify-center gap-1">
              <Icon icon="mdi:delete" class="text-sm" />
              <span class="text-[8px] font-black sm:hidden">Eliminar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="filteredHerramientas.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, filteredHerramientas.length) }} DE {{ filteredHerramientas.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
          <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL FORMULARIO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="isEditing ? 'mdi:pencil' : 'mdi:plus-circle'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">{{ isEditing ? 'Editar Herramienta' : 'Nueva Herramienta' }}</h3>
              <p class="text-[10px] text-white/70 font-bold">{{ isEditing ? 'Modifica los datos' : 'Registra una nueva herramienta' }}</p>
            </div>
            <button @click="showModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <form @submit.prevent="saveHerramienta" class="p-4 sm:p-6 space-y-5">
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  <Icon icon="mdi:qrcode" class="inline mr-1" /> Código *
                </label>
                <input 
                  v-model="form.codigo" 
                  type="text" 
                  required 
                  class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                  placeholder="Ej: HER-001"
                />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  <Icon icon="mdi:tools" class="inline mr-1" /> Nombre *
                </label>
                <input 
                  v-model="form.nombre" 
                  type="text" 
                  required 
                  class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all"
                  placeholder="Nombre de la herramienta"
                />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  <Icon icon="mdi:format-text" class="inline mr-1" /> Descripción
                </label>
                <textarea 
                  v-model="form.descripcion" 
                  rows="2"
                  class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                  placeholder="Descripción detallada..."
                />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  <Icon icon="mdi:store-marker" class="inline mr-1" /> Sede *
                </label>
                <select 
                  v-model="form.marca" 
                  required 
                  class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">Seleccionar sede...</option>
                  <option v-for="sede in sedesList" :key="sede.idSede" :value="sede.nombre">
                    {{ sede.nombre }}
                  </option>
                </select>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                    <Icon icon="mdi:package" class="inline mr-1" /> Stock
                  </label>
                  <input 
                    v-model.number="form.stock" 
                    type="number" 
                    min="0"
                    class="w-full px-3 sm:px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-100 dark:border-emerald-800/30 focus:border-emerald-500 rounded-xl text-xs font-black text-emerald-700 dark:text-emerald-300 outline-none transition-all"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                    <Icon icon="mdi:state-machine" class="inline mr-1" /> Estado
                  </label>
                  <select 
                    v-model="form.estado" 
                    class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                    <option value="Mantenimiento">Mantenimiento</option>
                  </select>
                </div>
              </div>
            </div>
            <p class="text-[8px] text-gray-400 italic">* Esta herramienta se registrará sin Kardex</p>
            <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button type="button" @click="showModal = false" class="w-full sm:flex-1 py-3 border-2 border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button type="submit" :disabled="guardando" class="w-full sm:flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-emerald-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                <Icon :icon="guardando ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': guardando}" />
                {{ guardando ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Registrar') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL DE INFORMACIÓN -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showInfoModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="showInfoModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-amber-600 to-orange-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:information-outline" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">Información de la Herramienta</h3>
              <p class="text-[10px] text-white/70 font-bold">Detalles completos</p>
            </div>
            <button @click="showInfoModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div v-if="infoHerramienta" class="p-4 sm:p-6 space-y-4">
            <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-3">
              <div class="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 tracking-widest uppercase">Código</span>
                <span class="font-black text-gray-900 dark:text-white text-sm">{{ infoHerramienta.codigo }}</span>
              </div>
              <div class="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 tracking-widest uppercase">Nombre</span>
                <span class="font-black text-gray-900 dark:text-white text-sm">{{ infoHerramienta.nombre }}</span>
              </div>
              <div class="flex justify-between items-start pb-2 border-b border-gray-200 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 tracking-widest uppercase">Descripción</span>
                <span class="font-medium text-gray-700 dark:text-gray-300 text-xs text-right max-w-[180px]">{{ infoHerramienta.descripcion || 'Sin descripción' }}</span>
              </div>
              <div class="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 tracking-widest uppercase">Sede</span>
                <span class="font-black text-gray-900 dark:text-white text-sm">{{ infoHerramienta.marca || '-' }}</span>
              </div>
              <div class="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 tracking-widest uppercase">Stock</span>
                <span :class="['font-black text-sm', infoHerramienta.stock > 0 ? 'text-gray-900 dark:text-white' : 'text-rose-500']">{{ infoHerramienta.stock }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[10px] font-black text-gray-400 tracking-widest uppercase">Estado</span>
                <span :class="[
                  'px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border shadow-sm',
                  infoHerramienta.estado === 'Activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' :
                  infoHerramienta.estado === 'Mantenimiento' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' :
                  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
                ]">
                  {{ infoHerramienta.estado }}
                </span>
              </div>
            </div>
            <button @click="showInfoModal = false" class="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { Icon } from '@iconify/vue';
import { getHerramientas, createHerramienta, updateHerramienta, deleteHerramienta } from '@/api/herramientas';
import { obtenerSedes } from '@/api/sede';
import Swal from 'sweetalert2';

// State
const todasLasHerramientas = ref([]);
const sedesList = ref([]);
const searchQuery = ref('');
const filtroEstado = ref('');
const showModal = ref(false);
const showInfoModal = ref(false);
const isEditing = ref(false);
const guardando = ref(false);
const cargando = ref(false);
const infoHerramienta = ref(null);

// Paginación
const currentPage = ref(1);
const itemsPerPage = 5;
const maxVisiblePages = 10;

// Computed
const herramientasTaller = computed(() => todasLasHerramientas.value);

const filteredHerramientas = computed(() => {
  let result = herramientasTaller.value;
  
  if (filtroEstado.value) {
    result = result.filter(h => h.estado === filtroEstado.value);
  }
  
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter(h => 
      h.nombre?.toLowerCase().includes(q) ||
      h.codigo?.toLowerCase().includes(q) ||
      (h.marca && h.marca.toLowerCase().includes(q))
    );
  }
  
  return result;
});

const herramientasActivas = computed(() => 
  herramientasTaller.value.filter(h => h.estado === 'Activo').length
);

const herramientasMantenimiento = computed(() => 
  herramientasTaller.value.filter(h => h.estado === 'Mantenimiento').length
);

const totalPages = computed(() => Math.ceil(filteredHerramientas.value.length / itemsPerPage));

const paginatedHerramientas = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredHerramientas.value.slice(start, end);
});

const displayedPages = computed(() => {
  const total = totalPages.value;
  const max = maxVisiblePages;
  const current = currentPage.value;
  
  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  
  let start = Math.max(1, current - Math.floor(max / 2));
  let end = start + max - 1;
  
  if (end > total) {
    end = total;
    start = Math.max(1, end - max + 1);
  }
  
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// Form
const form = reactive({
  idHerramienta: null,
  codigo: '',
  nombre: '',
  descripcion: '',
  marca: '',
  stock: 0,
  estado: 'Activo'
});

// Methods
const goToFirstPage = () => { currentPage.value = 1; };
const goToLastPage = () => { currentPage.value = totalPages.value; };

const fetchHerramientasTaller = async () => {
  cargando.value = true;
  try {
    const response = await getHerramientas();
    todasLasHerramientas.value = Array.isArray(response) ? response : (response?.data || []);
  } catch (error) {
    console.error('Error al cargar herramientas:', error);
    toast('Error al cargar datos', 'error');
  } finally {
    cargando.value = false;
  }
};

const fetchSedes = async () => {
  try {
    const response = await obtenerSedes();
    sedesList.value = response.success ? (response.data || []) : [];
  } catch (error) {
    console.error('Error al cargar sedes:', error);
  }
};

const openCreateModal = () => {
  isEditing.value = false;
  Object.assign(form, {
    idHerramienta: null, 
    codigo: '', 
    nombre: '', 
    descripcion: '', 
    marca: '', 
    stock: 0, 
    estado: 'Activo'
  });
  showModal.value = true;
};

const openEditModal = (h) => {
  isEditing.value = true;
  Object.assign(form, { ...h });
  showModal.value = true;
};

const openInfoModal = (h) => {
  infoHerramienta.value = h;
  showInfoModal.value = true;
};

const saveHerramienta = async () => {
  guardando.value = true;
  try {
    const dataToSend = {
      codigo: form.codigo,
      nombre: form.nombre,
      descripcion: form.descripcion || '',
      marca: form.marca,
      stock: form.stock || 0,
      estado: form.estado
    };

    if (isEditing.value) {
      await updateHerramienta(form.idHerramienta, dataToSend);
      toast('Herramienta actualizada con éxito');
    } else {
      await createHerramienta(dataToSend);
      toast('Herramienta registrada con éxito');
    }

    showModal.value = false;
    await fetchHerramientasTaller();
  } catch (error) {
    console.error('Error al guardar:', error);
    toast(error.message || 'Error al guardar', 'error');
  } finally {
    guardando.value = false;
  }
};

const confirmDeleteHerramienta = async (h) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `Se eliminará la herramienta "${h.nombre}". Esta acción no se deshace.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#f43f5e',
    cancelButtonColor: '#4f46e5',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    try {
      await deleteHerramienta(h.idHerramienta);
      toast('Herramienta eliminada correctamente');
      await fetchHerramientasTaller();
    } catch (error) {
      toast('Error al eliminar', 'error');
    }
  }
};

const toast = (message, icon = 'success') => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title: message,
    customClass: { popup: 'rounded-2xl' }
  });
};

onMounted(async () => {
  await Promise.all([fetchSedes(), fetchHerramientasTaller()]);
});
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
</style>